import type { PropertyPath, MergeWithCustomizer } from 'lodash'
import type { ApiRegister, ApiExecutor, ApiOptions, RequestParams } from './declare'
import type { InterceptorConfig } from './interceptor'
import axios, { axiosSpace, axiosBase } from './axios'
import * as interceptor from './interceptor'
import apiServers from './services'
import { apiConfig } from './config'
import { set, pick, mergeWith, unset, isEmpty } from 'lodash'
import md5 from 'md5'

class MakeApi {
  api: { [field: string]: ApiExecutor }
  paths: { [field: string]: string }
  requestParams: RequestParams
  throttleApis: string[]

  _mergeWith: MergeWithCustomizer

  constructor (config: ApiRegister) {
    this.api = {}
    this.paths = {}
    this.requestParams = {}
    this.throttleApis = []

    this._mergeWith = (ov, sv, key, object, source) => {
      // sv 为 undefined 时，默认 loadsh 是保持 ov 值的，这边设置为清除目标对象 key 值
      if (sv === undefined) {
        delete object[key]
      }
    }

    this.register(config)
    this.setInterceptor()
  }

  /**
   * 设置通用请求参数
   * @param params 
   */
  setRequestParams (params: RequestParams) {
    mergeWith(this.requestParams, params, this._mergeWith)
  }

  /**
   * 删除通用请求参数
   * @param path 
   */
  unsetRequestParams (path: PropertyPath) {
    unset(this.requestParams, path)
  }

  /**
   * 替换 services -> common.js 文件中的 path 属性中定义的字段
   * 例如：/text/path/:category，如果请求参数中存在 { category: 'baseType' }
   * 那么该请求路径会被替换为：/text/path/baseType
   * @param url 
   * @param payload 
   * @returns 
   */
  generateUrl (url: string, payload: any) {
    return url.replace(/:([\w\d]+)/ig, (reg, key) => {
      const value = payload[key]
      delete payload[key]
      return value
    })
  }

  /**
   * 决定 axios 请求的载体是 data(POST等) 还是 params(GET)
   * @param options 
   * @param payload 
   * @returns 
   */
  generateAxios (options: ApiOptions, payload: any) {
    const { dataMethodsType = ['POST', 'PUT', 'PATCH', 'DELETE'], noAttachedData } = options

    const { headers, commonPayload, dataPayload, paramsPayload } = this.requestParams
    if (dataMethodsType.includes(options.method?.toUpperCase())) {
      options.data = noAttachedData ? payload : mergeWith({}, commonPayload, payload, this._mergeWith)
    } else {
      options.params = noAttachedData ? payload : mergeWith({}, commonPayload, payload, this._mergeWith)
    }
    // dataPayload 整合进 POST 等请求中
    dataPayload && set(options, 'data', mergeWith({}, dataPayload, options.data, this._mergeWith))
    // paramsPayload 整合进 GET 请求中
    paramsPayload && set(options, 'params', mergeWith({}, paramsPayload, options.params, this._mergeWith))
    // 添加统一的认证token
    options.headers = mergeWith({}, headers, options.headers, this._mergeWith)

    return options
  }

  /**
   * 注册接口
   * @param config 
   */
  register (config: ApiRegister) {
    for (const [key, value] of Object.entries(config)) {
      value.forEach(api => {
        const { name, method, path, params, axiosOptions } = api

        const apiName = `${key}${apiConfig.sep}${name}`

        this.api[apiName] = (data, options, getCancelToken) => {
          const payload = mergeWith({}, params, data, this._mergeWith)
          const url = this.generateUrl(path, payload)
          const baseURL = axiosBase(apiConfig.prefixPath)

          // axios 配置
          const _options = mergeWith({
            baseURL,
            url,
            method,
            cancelToken: new axiosSpace.CancelToken(function (cancel) {
              /**
               * 用于取消 axios 请求，
               * 通过 getCancelToken 方法将 cancel 实例传出去，
               * 调用 cancel.cancel() 就能取消请求
               */
              getCancelToken?.(cancel)
            })
          }, axiosOptions, options, this._mergeWith)
          // axios 数据
          const _payload = _options.noPickParams || isEmpty(params) ? payload : pick(payload, Object.keys(params))
          // axios 请求参数
          const axiosData = this.generateAxios(_options, _payload)
          // 请求唯一标识
          const reqMD5 = md5(JSON.stringify(axiosData))
          // 节流
          if (axiosData.throttle && this.throttleApis.indexOf(reqMD5) !== -1) {
            return Promise.reject(`Throttle: ${path}`)
          }
          this.throttleApis.push(reqMD5)
          return axios(axiosData).finally(() => {
            const findIndex = this.throttleApis.indexOf(reqMD5)
            this.throttleApis.splice(findIndex, 1)
          })
        }
        this.paths[apiName] = path
      })
    }
  }

  ejectRequest?: number
  ejectResponse?: number

  /**
   * 自定义拦截器
   * @param config 
   */
  setInterceptor (config?: InterceptorConfig) {
    // 清除之前设置的拦截器
    this.ejectRequest !== undefined && axiosSpace.Request.eject(this.ejectRequest)
    this.ejectResponse !== undefined && axiosSpace.Response.eject(this.ejectResponse)
    // 定义新的拦截器
    this.ejectRequest = axiosSpace.Request.use(interceptor.reqSuccess(config?.reqSuccess), interceptor.reqFail(config?.reqFail))
    this.ejectResponse = axiosSpace.Response.use(interceptor.resSuccess(config?.resSuccess), interceptor.resFail(config?.resFail))
  }
}

export const api = new MakeApi(apiServers)
export default api['api']